/**
 * Build status-audit URLs that work in new tabs (query/path params, not router state).
 */

const appendParam = (params, key, value) => {
    if (value == null || value === "") return;
    params.set(key, String(value));
};

/** @typedef {import('react-router-dom').Location} RouterLocation */

/**
 * Resolve status-audit context from router state (same-tab) or URL (new tab).
 * @param {RouterLocation | { state?: object; search?: string }} location
 */
export function resolveStatusAuditContext(location) {
    const navState = location?.state ?? {};
    const params = new URLSearchParams(location?.search ?? "");

    const fromStateOrQuery = (stateKey, queryKey) =>
        navState[stateKey] ?? params.get(queryKey) ?? undefined;

    return {
        bookingId: fromStateOrQuery("bookingId", "bookingId"),
        FILENUMBER: fromStateOrQuery("FILENUMBER", "fileNumber"),
        companyId: fromStateOrQuery("companyId", "companyId"),
        customerName: fromStateOrQuery("customerName", "customerName"),
        createdBy: fromStateOrQuery("createdBy", "createdBy"),
        createdOn: fromStateOrQuery("createdOn", "createdOn"),
        carrierBookingNumber: fromStateOrQuery(
            "carrierBookingNumber",
            "carrierBookingNumber",
        ),
    };
}

/**
 * @param {{ shipment?: object; isImport?: boolean; companyId?: string|number }} options
 * @returns {string | null} App-relative path, or null if booking id is missing.
 */
export function getStatusAuditPath({ shipment, isImport = false, companyId }) {
    const bookingId = shipment?.id;
    if (!bookingId) return null;

    if (isImport) {
        const resolvedCompanyId =
            shipment?.COMPANY_ID ?? companyId ?? undefined;
        const qs = resolvedCompanyId
            ? `?companyId=${encodeURIComponent(resolvedCompanyId)}`
            : "";
        return `/import-booking/status-audit/${bookingId}${qs}`;
    }

    const params = new URLSearchParams();
    appendParam(params, "bookingId", bookingId);
    appendParam(params, "fileNumber", shipment?.fileNumber);
    appendParam(params, "companyId", shipment?.COMPANY_ID);
    appendParam(params, "customerName", shipment?.customerName);
    appendParam(params, "createdBy", shipment?.createdBy);
    appendParam(params, "createdOn", shipment?.createdOn);
    appendParam(
        params,
        "carrierBookingNumber",
        shipment?.carrierBookingNumber,
    );

    return `/status-audit?${params.toString()}`;
}

/**
 * Open status audit in a new browser tab.
 * @param {{ shipment?: object; isImport?: boolean; companyId?: string|number }} options
 */
export function openStatusAuditInNewTab(options) {
    const path = getStatusAuditPath(options);
    if (!path) return;

    window.open(
        `${window.location.origin}${path}`,
        "_blank",
        "noopener,noreferrer",
    );
}
